<template>
  <!-- Overlay -->
  <div
    v-if="visible"
    class="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-30 tw-z-50 tw-flex tw-items-center tw-justify-center"
    @click.self="$emit('close')"
  >
    <div class="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-2xl tw-max-h-[80vh] tw-flex tw-flex-col tw-mx-4">
      <!-- Header -->
      <div class="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
        <h3 class="tw-font-semibold tw-text-gray-800">Commando historie<span v-if="isC4a" class="tw-font-normal tw-text-gray-500"> · Cast4all</span></h3>
        <button
          @click="$emit('close')"
          class="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
        >
          <svg class="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="tw-overflow-y-auto tw-flex-1 tw-px-5 tw-py-3">
        <div v-if="isLoading" class="tw-flex tw-items-center tw-justify-center tw-py-8 tw-text-gray-400 tw-text-sm">
          <span class="tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-mr-2" />
          Laden...
        </div>

        <div v-else-if="!commands || commands.length === 0" class="tw-py-8 tw-text-center tw-text-gray-400 tw-text-sm">
          Geen commando's gevonden voor deze meter.
        </div>

        <table v-else class="tw-w-full tw-text-sm">
          <thead>
            <tr class="tw-text-left tw-text-xs tw-text-gray-500 tw-uppercase tw-tracking-wider">
              <th class="tw-pb-2 tw-pr-3">Tijdstip</th>
              <th class="tw-pb-2 tw-pr-3">Type</th>
              <th class="tw-pb-2 tw-pr-3">Status</th>
              <th class="tw-pb-2">Resultaat</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="cmd in commands"
              :key="cmd.id"
              class="tw-border-t tw-border-gray-50 hover:tw-bg-gray-50"
            >
              <td class="tw-py-2 tw-pr-3 tw-text-gray-600 tw-whitespace-nowrap tw-font-mono tw-text-xs">
                {{ formatDateTime(cmd.performed_at) }}
              </td>
              <td class="tw-py-2 tw-pr-3">
                <span
                  class="tw-text-xs tw-px-1.5 tw-py-0.5 tw-rounded tw-font-medium"
                  :class="commandTypeBadge(cmd.command_type)"
                >{{ cmd.command_type }}</span>
              </td>
              <td class="tw-py-2 tw-pr-3">
                <span
                  class="tw-text-xs tw-font-medium"
                  :class="cmd.status === 'success' ? 'tw-text-green-600' : cmd.status === 'failed' ? 'tw-text-red-600' : 'tw-text-gray-500'"
                >{{ cmd.status }}</span>
              </td>
              <td class="tw-py-2 tw-text-xs tw-text-gray-500 tw-max-w-xs tw-truncate">
                {{ cmd.command_result || '—' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'
import { formatDateTimeNL as formatDateTime } from './formatters'

const props = defineProps({
  serialNumber: { type: String, required: true },
  visible: { type: Boolean, default: false },
  // Cast4all meters: history from the Cast4all API (meter and communicator commands) in the same fields
  isC4a: { type: Boolean, default: false },
})

defineEmits(['close'])

const { data: commandsData, isLoading } = useQuery({
  queryKey: computed(() => ['meterCommands', props.serialNumber, props.isC4a]),
  queryFn: async () => {
    const path = props.isC4a ? 'c4a/commands' : 'commands'
    const result = await fetchData(`/eleena/api/v1/meters/${props.serialNumber}/${path}?limit=100`)
    return result?.status === 'OK' ? result.data : []
  },
  refetchOnWindowFocus: false,
  enabled: computed(() => props.visible),
})

const commands = computed(() => commandsData.value || [])

// formatDateTime imported from formatters.js

const commandTypeBadge = (type) => {
  const t = (type || '').toLowerCase()
  if (t.includes('poll') || t.includes('read')) return 'tw-bg-blue-100 tw-text-blue-700'
  if (t.includes('ping') || t.includes('connect')) return 'tw-bg-green-100 tw-text-green-700'
  if (t.includes('breaker') || t.includes('switch')) return 'tw-bg-amber-100 tw-text-amber-700'
  if (t.includes('firmware') || t.includes('upgrade')) return 'tw-bg-purple-100 tw-text-purple-700'
  return 'tw-bg-gray-100 tw-text-gray-600'
}
</script>
